import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";
import { api } from "../lib/api";

const CATEGORY_META = {
  invisible: {
    label: "The Hidden Business",
    tint: { bg: "#1a0808", color: "#eb5a5a" },
    tagline: "There's a real business hiding in what you already know — most people just haven't seen it yet.",
  },
  underweighted: {
    label: "The Underclaimed Business",
    tint: { bg: "#1a0f08", color: "#ba7a30" },
    tagline: "You're doing this — but the people who'd pay for it don't yet see it the way you do.",
  },
  unfocused: {
    label: "The Unfocused Business",
    tint: { bg: "#14140a", color: "#bba030" },
    tagline: "You have the substance. The story of what you actually do is scattered across too many things.",
  },
  unpackaged: {
    label: "The Unpackaged Business",
    tint: { bg: "#0a1a0a", color: "#3aab5a" },
    tagline: "You know what people want. You just haven't turned it into something they can buy yet.",
  },
  unamplified: {
    label: "The Unamplified Business",
    tint: { bg: "#1a0a08", color: "#FF6575" },
    tagline: "You've built the thing. The next lever is getting it in front of the people who need it.",
  },
};

export default function Interview() {
  const [phase, setPhase] = useState("landing"); // landing | chatting | complete
  const [email, setEmail] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role: "user"|"assistant", content}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const scrollRef = useRef(null);

  // Capture UTMs from the URL once at mount so refill/back-button doesn't lose them.
  const utm = useMemo(() => {
    if (typeof window === "undefined") return { utm_source: "", utm_campaign: "" };
    const q = new URLSearchParams(window.location.search);
    return {
      utm_source: (q.get("utm_source") || "").trim().slice(0, 120),
      utm_campaign: (q.get("utm_campaign") || "").trim().slice(0, 120),
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const startInterview = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setStarting(true);
    try {
      const { data } = await api.post("/interview/start", {
        email: email.trim(),
        utm_source: utm.utm_source || undefined,
        utm_campaign: utm.utm_campaign || undefined,
      });
      setSessionId(data.session_id);
      setPhase("chatting");
      // Kick off the interview with an opening message from the user side.
      // Atlas will respond with the Stage 1 question.
      await sendMessage(
        "I'm ready. I want to figure out what I could build from what I already know.",
        data.session_id,
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not start the interview. Please try again.";
      toast.error(detail);
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async (text, sid = sessionId) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || !sid) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    try {
      const { data } = await api.post("/interview/message", { session_id: sid, message: trimmed });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.status === "complete") {
        setDiagnosis(data.diagnosis);
        setPhase("complete");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Message could not be sent. Please try again.";
      toast.error(detail);
      // Roll back the optimistic user message on hard failure.
      setMessages((m) => m.slice(0, -1));
      setInput(trimmed);
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  return (
    <Layout>
      <section
        style={{ position: "relative", padding: "120px 20px 60px", minHeight: "100vh", background: "#0d0d0d" }}
        data-testid="interview-page"
      >
        <HeroBg />
        <div style={{ position: "relative", zIndex: 2, maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <EyebrowPill>Guided Interview · With Atlas</EyebrowPill>
            <h1
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontWeight: 900,
                fontSize: "clamp(38px, 6vw, 60px)",
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                color: "#fff",
                margin: "18px 0 12px",
              }}
              data-testid="interview-title"
            >
              You already know something worth <span style={{ color: "#FF6575" }}>building a business around.</span>
            </h1>
            <p style={{ color: "#9a9a9a", fontSize: 15, lineHeight: 1.6, maxWidth: 560, margin: "0 auto" }}>
              Answer a few questions and find out what it could be. A short guided conversation with Atlas —
              six quick stages, and at the end you&apos;ll see what you could actually build from what you
              already know.
            </p>
          </div>

          {phase === "landing" && (
            <form onSubmit={startInterview} className="tag-card" style={{ padding: 22 }} data-testid="interview-start-form">
              <label style={{ color: "#e6e6e6", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                Email
              </label>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourpractice.com"
                data-testid="interview-email-input"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  marginTop: 10,
                  padding: "14px 16px",
                  background: "#0a0a0a",
                  color: "#fff",
                  border: "1px solid #262626",
                  borderRadius: 10,
                  fontSize: 15,
                  outline: "none",
                }}
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={starting || !email.trim()}
                data-testid="interview-start-btn"
                style={{ width: "100%", marginTop: 14 }}
              >
                {starting ? "Starting…" : "Begin the interview"}
              </button>
              <p style={{ color: "#777", fontSize: 11, marginTop: 10, textAlign: "center" }}>
                Takes about 8 minutes. Your answers stay private. We&apos;ll email you a copy of what you could build.
              </p>
            </form>
          )}

          {phase === "chatting" && (
            <div className="tag-card" style={{ padding: 0, overflow: "hidden" }} data-testid="interview-chat">
              <div
                ref={scrollRef}
                style={{
                  padding: "22px 22px 12px",
                  maxHeight: "58vh",
                  overflowY: "auto",
                }}
              >
                {messages.map((m, idx) => (
                  <div
                    key={idx}
                    data-testid={`interview-msg-${m.role}-${idx}`}
                    style={{
                      display: "flex",
                      justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "82%",
                        padding: "12px 14px",
                        borderRadius: 14,
                        background: m.role === "user" ? "#FF6575" : "#161616",
                        color: m.role === "user" ? "#0d0d0d" : "#eaeaea",
                        fontSize: 14,
                        lineHeight: 1.55,
                        whiteSpace: "pre-wrap",
                        border: m.role === "user" ? "none" : "1px solid #232323",
                        fontWeight: m.role === "user" ? 500 : 400,
                      }}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }} data-testid="interview-thinking">
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: 14,
                        background: "#161616",
                        border: "1px solid #232323",
                        color: "#888",
                        fontSize: 13,
                      }}
                    >
                      <span className="tag-pulse" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#FF6575", marginRight: 8, verticalAlign: "middle" }} />
                      Atlas is thinking…
                    </div>
                  </div>
                )}
              </div>
              <form
                onSubmit={onSubmit}
                style={{ borderTop: "1px solid #1e1e1e", padding: 14, display: "flex", gap: 10, background: "#0a0a0a" }}
              >
                <textarea
                  rows={2}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      onSubmit(e);
                    }
                  }}
                  disabled={sending}
                  placeholder="Type your answer… (Enter to send · Shift+Enter for newline)"
                  data-testid="interview-input"
                  style={{
                    flex: 1,
                    background: "#0d0d0d",
                    color: "#fff",
                    border: "1px solid #262626",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    resize: "none",
                    outline: "none",
                    fontFamily: "'Space Grotesk', system-ui, sans-serif",
                  }}
                />
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={sending || !input.trim()}
                  data-testid="interview-send-btn"
                  style={{ minWidth: 90 }}
                >
                  Send
                </button>
              </form>
            </div>
          )}

          {phase === "complete" && (
            <ResultDisplay diagnosis={diagnosis} email={email} />
          )}
        </div>
      </section>
    </Layout>
  );
}

const ResultDisplay = ({ diagnosis, email }) => {
  const cat = (diagnosis?.authority_score_category || "invisible").toLowerCase();
  const meta = CATEGORY_META[cat] || CATEGORY_META.invisible;
  return (
    <div data-testid="interview-result">
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div
          data-testid="interview-result-kicker"
          style={{
            display: "inline-block",
            padding: "6px 14px",
            background: "#0a0a0a",
            color: "#FF6575",
            border: "1px solid #1e1e1e",
            borderRadius: 20,
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.09em",
            marginBottom: 12,
          }}
        >
          What You Could Build
        </div>
        <div>
          <span
            data-testid="interview-category"
            style={{
              display: "inline-block",
              padding: "6px 14px",
              background: meta.tint.bg,
              color: meta.tint.color,
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {meta.label}
          </span>
        </div>
      </div>
      <div
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontWeight: 900,
          fontSize: "clamp(30px, 4.5vw, 44px)",
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          color: "#fff",
          textAlign: "center",
          marginBottom: 22,
        }}
      >
        {meta.tagline}
      </div>

      {diagnosis?.gap_moment && (
        <div className="tag-card" style={{ marginBottom: 12 }}>
          <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>WHEN IT WORKED</div>
          <p style={{ color: "#ddd", fontSize: 14, lineHeight: 1.6, margin: 0 }} data-testid="interview-gap-moment">{diagnosis.gap_moment}</p>
        </div>
      )}

      {diagnosis?.proof_quote && (
        <div className="tag-card" style={{ marginBottom: 12 }}>
          <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>THE QUIET WIN</div>
          <p style={{ color: "#ddd", fontSize: 14, lineHeight: 1.6, margin: 0, fontStyle: "italic" }} data-testid="interview-proof">“{diagnosis.proof_quote}”</p>
        </div>
      )}

      {diagnosis?.pattern_insight && (
        <div className="tag-card" style={{ marginBottom: 12 }}>
          <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>WHAT YOU ACTUALLY KNOW HOW TO DO</div>
          <p style={{ color: "#ddd", fontSize: 14, lineHeight: 1.6, margin: 0 }} data-testid="interview-pattern">{diagnosis.pattern_insight}</p>
        </div>
      )}

      {diagnosis?.audience_segment && (
        <div className="tag-card" style={{ marginBottom: 12 }}>
          <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>WHO WOULD PAY FOR THIS</div>
          <p style={{ color: "#ddd", fontSize: 14, lineHeight: 1.6, margin: 0 }} data-testid="interview-audience">{diagnosis.audience_segment}</p>
        </div>
      )}

      {diagnosis?.positioning_statement && (
        <div className="tag-card" style={{ marginBottom: 20 }}>
          <div style={{ color: "#FF6575", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>HOW YOU&apos;D SAY IT</div>
          <p style={{ color: "#ddd", fontSize: 14, lineHeight: 1.6, margin: 0, fontStyle: "italic" }} data-testid="interview-positioning">“{diagnosis.positioning_statement}”</p>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <a href="/audit" data-testid="interview-cta-audit" className="btn-primary" style={{ textAlign: "center" }}>
          See how to package this
        </a>
        <a
          href="/blueprint"
          data-testid="interview-cta-blueprint"
          className="btn-primary"
          style={{ textAlign: "center", background: "transparent", color: "#FF6575", border: "1px solid #FF6575" }}
        >
          Build the full playbook
        </a>
      </div>
      <p style={{ color: "#777", fontSize: 11, textAlign: "center", marginTop: 12 }}>
        We&apos;ve also emailed <span style={{ color: "#aaa" }}>{email}</span> — a copy of what you could build, and how to take the next step.
      </p>
    </div>
  );
};
